import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpHeaders, HttpParams, HttpResponse } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from '@env/environment';

export class HttpRequestOptions {
    body?: unknown;
    headers?: HttpHeaders;
    observe?: 'response' = 'response';
    params?: HttpParams;
    reportProgress?: boolean = false;
    responseType?: 'arraybuffer' | 'json' | 'blob';
    withCredentials?: boolean = false;
}

/**
 * @deprecated Use Angular's `HttpClient` directly instead.
 * API URLs are now handled by `apiUrlInterceptor`.
 * This service is retained temporarily while existing consumers are migrated.
 */
@Injectable({
    providedIn: 'root',
})
export class AuthHttpService {
    private http = inject(HttpClient);

    public get(url: string, type: 'blob', opts?: Partial<HttpRequestOptions>): Observable<HttpResponse<Blob>>;
    public get(
        url: string,
        type: 'arraybuffer',
        opts?: Partial<HttpRequestOptions>,
    ): Observable<HttpResponse<ArrayBuffer>>;
    public get<T>(url: string, type: 'json', opts?: Partial<HttpRequestOptions>): Observable<HttpResponse<T>>;
    public get(url: string, type: 'text', opts?: Partial<HttpRequestOptions>): Observable<HttpResponse<string>>;
    public get<T>(url: string, type?: any, opts?: Partial<HttpRequestOptions>): Observable<HttpResponse<T>>;

    public get(url: string, type: any, opts?: Partial<HttpRequestOptions>) {
        type = type || 'json';
        return this.request('GET', url, type as any, opts) as any;
    }

    public delete(
        url: string,
        body?: any,
        type?: 'blob',
        opts?: Partial<HttpRequestOptions>,
    ): Observable<HttpResponse<Blob>>;
    public delete(
        url: string,
        body?: any,
        type?: 'arraybuffer',
        opts?: Partial<HttpRequestOptions>,
    ): Observable<HttpResponse<ArrayBuffer>>;
    public delete<T>(
        url: string,
        body?: any,
        type?: 'json',
        opts?: Partial<HttpRequestOptions>,
    ): Observable<HttpResponse<T>>;
    public delete<T>(
        url: string,
        body?: any,
        type?: any,
        opts?: Partial<HttpRequestOptions>,
    ): Observable<HttpResponse<T>>;

    public delete(url: string, body?: any, type?: any, opts?: Partial<HttpRequestOptions>) {
        type = type || 'json';

        if (body) {
            opts = opts || {};
            opts.body = body;
        }

        return this.request('DELETE', url, type as any, opts) as any;
    }

    public post(
        url: string,
        body: any,
        type: 'blob',
        opts?: Partial<HttpRequestOptions>,
    ): Observable<HttpResponse<Blob>>;
    public post(
        url: string,
        body: any,
        type: 'arraybuffer',
        opts?: Partial<HttpRequestOptions>,
    ): Observable<HttpResponse<ArrayBuffer>>;
    public post<T>(
        url: string,
        body: any,
        type: 'json',
        opts?: Partial<HttpRequestOptions>,
    ): Observable<HttpResponse<T>>;
    public post<T>(url: string, body: any, type?: any, opts?: Partial<HttpRequestOptions>): Observable<HttpResponse<T>>;

    public post(url: string, body: any, type: any, opts?: Partial<HttpRequestOptions>) {
        opts = opts || {};
        opts.body = body;

        return this.request('POST', url, type as any, opts) as any;
    }

    public postForm(url: string, form: FormData) {
        const requestUrl = `${environment.apiUrl}${url}`;

        return this.http.post(requestUrl, form) as any;
    }

    public patch(
        url: string,
        body: any,
        type: 'blob',
        opts?: Partial<HttpRequestOptions>,
    ): Observable<HttpResponse<Blob>>;
    public patch(
        url: string,
        body: any,
        type: 'arraybuffer',
        opts?: Partial<HttpRequestOptions>,
    ): Observable<HttpResponse<ArrayBuffer>>;
    public patch<T>(
        url: string,
        body: any,
        type: 'json',
        opts?: Partial<HttpRequestOptions>,
    ): Observable<HttpResponse<T>>;
    public patch<T>(
        url: string,
        body: any,
        type?: any,
        opts?: Partial<HttpRequestOptions>,
    ): Observable<HttpResponse<T>>;

    public patch(url: string, body: any, type: any, opts?: Partial<HttpRequestOptions>) {
        opts = opts || {};
        opts.body = body;

        return this.request('PATCH', url, type as any, opts) as any;
    }

    public put(
        url: string,
        body: any,
        type: 'blob',
        opts?: Partial<HttpRequestOptions>,
    ): Observable<HttpResponse<Blob>>;
    public put(
        url: string,
        body: any,
        type: 'arraybuffer',
        opts?: Partial<HttpRequestOptions>,
    ): Observable<HttpResponse<ArrayBuffer>>;
    public put<T>(
        url: string,
        body: any,
        type: 'json',
        opts?: Partial<HttpRequestOptions>,
    ): Observable<HttpResponse<T>>;
    public put<T>(url: string, body: any, type?: any, opts?: Partial<HttpRequestOptions>): Observable<HttpResponse<T>>;

    public put(url: string, body: any, type: any, opts?: Partial<HttpRequestOptions>) {
        opts = opts || {};
        opts.body = body;

        return this.request('PUT', url, type as any, opts) as any;
    }

    private request(
        method: string,
        url: string,
        type: 'blob' | 'arraybuffer' | 'json',
        opts?: Partial<HttpRequestOptions>,
    ): Observable<any> {
        opts = opts || new HttpRequestOptions();
        opts.observe = opts.observe || 'response';
        opts.headers = opts.headers || new HttpHeaders();

        opts.responseType = opts.responseType || type;

        const requestUrl = `${environment.apiUrl}${url}`;

        return this.http.request(method, requestUrl, {
            headers: opts.headers,
            observe: 'response',
            body: opts.body,
            params: opts.params,
            reportProgress: opts.reportProgress,
            responseType: type,
            withCredentials: false,
        }) as any;
    }
}
